import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import useListTruncator from 'hooks/useListTruncator'
import type { ComponentProps, MouseEventHandler, ReactNode } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList
  from 'components/SearchKit/SearchTiles/shared/SearchTileStackAmenitiesList'
import type { SearchTileRowInclusion } from './SearchTileRowInclusionItem'

interface Props {
  features: Array<SearchTileRowInclusion>
  featuresType: 'inclusions' | 'amenities'
  onMoreClick?: () => void
  singleColumn?: boolean
  /**
   * Extra hidden items rendered outside this list (e.g. loyalty inclusions)
   * that should be counted in the "+N more" link total.
   */
  additionalHiddenCount?: number
  maxInclusionsToShow?: number
  overflowLabel?: string
  inclusionSize?: ComponentProps<typeof OfferInclusionItem>['size']
  leading?: ReactNode
}

export const MAX_INCLUSIONS_TO_SHOW = 6

function ExperimentalSearchTileFeaturesList({
  features,
  featuresType,
  onMoreClick,
  singleColumn,
  additionalHiddenCount = 0,
  maxInclusionsToShow = MAX_INCLUSIONS_TO_SHOW,
  overflowLabel,
  inclusionSize,
  leading,
}: Props) {
  const intl = useIntl()
  const [visibleFeatures, hiddenFeatures] = useListTruncator(maxInclusionsToShow, features)
  const handleMoreClick = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    onMoreClick?.()
  }, [onMoreClick])
  if (!leading && !visibleFeatures.length && !overflowLabel) return null

  return <Group direction="vertical" gap={4}>
    {leading}
    {featuresType === 'inclusions' && visibleFeatures.map(inclusion =>
      <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        lineClamp={inclusionSize === 'small' ? 1 : 2}
        size={inclusionSize}
      />,
    )}
    {featuresType === 'inclusions' && !!overflowLabel && <div>
      {!!onMoreClick && <TextLink size="small" onClick={handleMoreClick}>{overflowLabel}</TextLink>}
      {!onMoreClick && <BodyText variant="small" weight="bold">{overflowLabel}</BodyText>}
    </div>}
    {featuresType === 'inclusions' && !overflowLabel && !!(hiddenFeatures.length || additionalHiddenCount) && <div>
      {!!onMoreClick && <TextLink size="small" onClick={handleMoreClick}>{intl.formatMessage({
        id: 'JLiExn3LXJlJLEAWSb4J',
        defaultMessage: '+ {count} more',
        description: 'Link to show more truncated inclusions on search tile',
      }, { count: hiddenFeatures.length + additionalHiddenCount })}
      </TextLink>}
      {!onMoreClick && <BodyText variant="small" weight="bold">{intl.formatMessage({
        id: 'Itu8L9SiB1_uXFeRj72U',
        defaultMessage: '+ {count} more',
        description: 'Text showing count of additional truncated inclusions on search tile',
      }, { count: hiddenFeatures.length + additionalHiddenCount })}
      </BodyText>}
    </div>}
    {featuresType === 'amenities' &&
      <SearchTileStackAmenitiesList
        amenities={features}
        truncateAfter={maxInclusionsToShow}
        singleColumn={singleColumn}
        variant={inclusionSize === 'small' ? 'small' : undefined}
      />}
  </Group>
}

export default ExperimentalSearchTileFeaturesList
